import * as ngCdkDragDrop from '@angular/cdk/drag-drop'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'

import * as glFilter from '../../component'
import { AtnGlFilterStore } from '../../store'

/**
 * Determines how deep a user can nest statements
 *
 * `0` = no nesting
 */
const MAX_DEPTH = 2

@ngCore.Component({
  imports: [
    ngCdkDragDrop.DragDropModule,
    // Circular dependency
    ngCore.forwardRef(() => glFilter.Component),
  ],
  selector: 'atn-rate-gl-filter-statement',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /* --------------------------------- Imports -------------------------------- */

  /**
   * Exposes {@link ents.extra.rate.gl.Filter.Statement.Connector} to the HTML
   */
  public readonly Connector = ents.extra.rate.gl.Filter.Statement.Connector

  /**
   * @see {@link MAX_DEPTH}
   */
  public readonly maxDepth = MAX_DEPTH

  /* ------------------------------- Injections ------------------------------- */

  /** @see {@link AtnGlFilterStore} */
  public readonly store = ngCore.inject(AtnGlFilterStore)

  /* --------------------------------- Inputs --------------------------------- */

  /**
   * {@link ents.extra.rate.gl.Filter.Statement} being rendered
   */
  public readonly statementSg =
    ngCore.input.required<ents.extra.rate.gl.Filter.Statement>({
      alias: 'statement',
    })

  /**
   * Identifies this {@link ents.extra.rate.gl.Filter.Statement} within the
   * current {@link ents.extra.rate.gl.Filter}
   */
  public readonly idSg = ngCore.input.required<string>({
    alias: 'id',
  })

  /* --------------------------------- Outputs -------------------------------- */

  /**
   * Emits changes to this {@link ents.extra.rate.gl.Filter.Statement}
   */
  public readonly statementChange =
    ngCore.output<ents.extra.rate.gl.Filter.Statement>()

  /* -------------------------------- Computed -------------------------------- */

  /**
   * Used in {@link addGroup}
   */
  public readonly depthSg = ngCore.computed((id = this.idSg()) =>
    this.store.getDepth(id),
  )

  /**
   * Used in {@link addGroup}
   */
  private readonly _defaultGroupFiltersSg = ngCore.computed<
    ents.extra.rate.gl.Filter.Statement['filters']
  >((defaultFilter = this.store.defaultFilterSg()) => [
    defaultFilter,
    defaultFilter,
  ])

  /* --------------------------------- Methods -------------------------------- */

  /**
   * Inverting the filter
   * {@link ents.extra.rate.gl.Filter.Statement.Connector connectors} upon
   * clicking a connector in the filter
   */
  public toggleConnector() {
    this.store.invertConnectors()
  }

  /**
   * Adds a new {@link ents.extra.rate.gl.Filter filter} to the existing
   * {@link ents.extra.rate.gl.Filter.Statement statement}
   */
  private _addFilter(filter: ents.extra.rate.gl.Filter) {
    const statement = this.statementSg()
    this.statementChange.emit({
      ...statement,
      filters: [...statement.filters, filter],
    })
  }

  /**
   * Substitutes old {@link ents.extra.rate.gl.Filter filter} in the
   * {@link ents.extra.rate.gl.Filter.Statement statement} with a new one after
   * change occured
   */
  public overwriteFilter(index: number, filter?: ents.extra.rate.gl.Filter) {
    if (filter) {
      const statement = this.statementSg()
      const filters = [...statement.filters]
      filters[index] = filter
      this.statementChange.emit({ ...statement, filters })
    }
  }

  /**
   * Adds a {@link ents.extra.rate.gl.Filter.Statement statement} inside of the
   * existing statement
   */
  public addGroup() {
    const statement = this.statementSg()
    this._addFilter({
      connector: ents.extra.rate.gl.Filter.Statement.Connector.inverse(
        statement.connector,
      ),
      filters: this._defaultGroupFiltersSg(),
    })
  }
}
